import { useState, useRef } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X, FileText, Loader2, Sparkles, Check, ArrowRight, ChevronDown, Download, Edit3, AlertTriangle } from "lucide-react";

const CRM_FIELDS = [
  { value: "first_name", label: "First name" },
  { value: "last_name", label: "Last name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "company", label: "Company / Practice" },
  { value: "job_title", label: "Job title" },
  { value: "specialty", label: "Specialty" },
  { value: "value", label: "Deal value" },
  { value: "title", label: "Lead title" },
  { value: "notes", label: "Notes" },
  { value: "source", label: "Lead source" },
  { value: "city", label: "City" },
  { value: "state", label: "State" },
  { value: "npi_number", label: "NPI number" },
  { value: "tags", label: "Tags" },
  { value: "skip", label: "Skip column" },
];

export default function CSVImportModal({ open, onClose, onImported, type = "leads" }) {
  const { token } = useAuth();
  const fileRef = useRef(null);
  const [step, setStep] = useState(1); // 1=upload, 2=mapping, 3=importing, 4=done
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [aiPowered, setAiPowered] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [editingCell, setEditingCell] = useState(null);

  const MOCK_CSV = `"First Name","Last Name","Email","Phone","Practice Name","Job Title","Specialty","Deal Value","Notes","Lead Source"
"Dr. Angela","Reeves","areeves@premierortho.com","(404) 555-1001","Premier Orthopedics","Physician","Orthopedics","125000","Interested in AI charting and billing","website"
"Brian","Kessler","bkessler@midwestfp.com","(312) 555-1002","Midwest Family Practice","Practice Manager","Family Medicine","75000","Referred by Valley Urgent Care","referral"
"Dr. Natasha","Volkov","nvolkov@heartcarectr.com","(713) 555-1003","Heart Care Center","Medical Director","Cardiology","200000","Needs CCM and RPM solution","conference"
"James","Whitfield","jwhitfield@coastalurg.com","(305) 555-1004","Coastal Urgent Care","CEO","Urgent Care","90000","Multiple locations, wants enterprise plan","sales_agent"
"Dr. Susan","Park","spark@metroderm.com","(646) 555-1005","Metro Dermatology","Dermatologist","Dermatology","55000","Interested in telehealth integration","website"
"Ricardo","Santos","rsantos@greenvalmed.com","(512) 555-1006","Green Valley Medical","Office Manager","Multi-specialty","160000","Current EHR is outdated, wants migration","referral"
"Dr. Helen","Cross","hcross@sunrisepeds.com","(615) 555-1007","Sunrise Pediatrics","Physician","Pediatrics","68000","Wants patient portal and billing","client_referral"
"Thomas","Nguyen","tnguyen@lakesideent.com","(612) 555-1008","Lakeside ENT Specialists","IT Director","ENT","110000","Looking to consolidate 3 systems into one","conference"`;

  if (!open) return null;

  const downloadMockCSV = () => {
    const blob = new Blob([MOCK_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "enable_healthcare_sample_leads.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Sample CSV downloaded");
  };

  const loadMockCSV = async () => {
    const { headers: hdrs, rows } = parseCSV(MOCK_CSV);
    const mockFile = new File([MOCK_CSV], "enable_healthcare_sample_leads.csv", { type: "text/csv" });
    setFile(mockFile);
    setHeaders(hdrs);
    setSampleRows(rows);
    setTotalRows(MOCK_CSV.split("\n").filter(l => l.trim()).length - 1);
    setAnalyzing(true);
    setStep(2);
    try {
      const res = await axios.post(`${API}/ai/analyze-csv-v2`, { headers: hdrs, sample_rows: rows }, { headers: { Authorization: `Bearer ${token}` } });
      setMapping(res.data.suggested_mapping || {});
      setAiPowered(res.data.ai_powered || false);
    } catch {
      const fallback = {};
      hdrs.forEach(h => { fallback[h] = "skip"; });
      setMapping(fallback);
    }
    setAnalyzing(false);
  };

  const parseCSV = (text) => {
    const lines = text.split("\n").filter(l => l.trim());
    if (lines.length < 2) return { headers: [], rows: [] };
    const hdrs = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
    const rows = lines.slice(1, 6).map(line => {
      const vals = [];
      let current = "";
      let inQuotes = false;
      for (const char of line) {
        if (char === '"') inQuotes = !inQuotes;
        else if (char === "," && !inQuotes) { vals.push(current.trim()); current = ""; }
        else current += char;
      }
      vals.push(current.trim());
      return vals;
    });
    return { headers: hdrs, rows };
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith(".csv")) { toast.error("Please upload a .csv file"); return; }
    setFile(f);
    const text = await f.text();
    const { headers: hdrs, rows } = parseCSV(text);
    if (hdrs.length === 0) { toast.error("Could not parse CSV headers"); return; }
    setHeaders(hdrs);
    setSampleRows(rows);
    const allLines = text.split("\n").filter(l => l.trim());
    setTotalRows(allLines.length - 1);
    // Run AI analysis
    setAnalyzing(true);
    setStep(2);
    try {
      const res = await axios.post(`${API}/ai/analyze-csv-v2`, { headers: hdrs, sample_rows: rows }, { headers: { Authorization: `Bearer ${token}` } });
      setMapping(res.data.suggested_mapping || {});
      setAiPowered(res.data.ai_powered || false);
    } catch {
      // Basic fallback mapping
      const fallback = {};
      hdrs.forEach(h => { fallback[h] = "skip"; });
      setMapping(fallback);
    }
    setAnalyzing(false);
  };

  const updateMapping = (header, field) => {
    setMapping(prev => ({ ...prev, [header]: field }));
  };

  const doImport = async () => {
    setImporting(true);
    setStep(3);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mapping", JSON.stringify(mapping));
      const res = await axios.post(`${API}/leads/import-csv`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      setStep(4);
      toast.success(`Imported ${res.data.imported} leads`);
    } catch (err) {
      toast.error("Import failed: " + (err.response?.data?.detail || "Unknown error"));
      setStep(2);
    }
    setImporting(false);
  };

  const close = () => {
    setStep(1); setFile(null); setHeaders([]); setSampleRows([]); setMapping({}); setResult(null); setTotalRows(0); setEditingCell(null);
    onClose();
    if (result?.imported > 0 && onImported) onImported();
  };

  const mappedCount = Object.values(mapping).filter(v => v !== "skip").length;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={close} data-testid="csv-import-modal">
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", backdropFilter: "blur(4px)" }} />
      <div onClick={e => e.stopPropagation()} style={{ position: "relative", width: "100%", maxWidth: 640, background: "var(--surface)", borderRadius: 14, border: "0.5px solid var(--border)", overflow: "hidden", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "0.5px solid var(--border)" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 650 }}>Import {type} from CSV</div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
              {step === 1 && "Upload a .csv file to import leads"}
              {step === 2 && `${aiPowered ? "AI-powered" : "Rule-based"} column mapping — ${mappedCount} of ${headers.length} columns mapped`}
              {step === 3 && "Importing leads..."}
              {step === 4 && "Import complete"}
            </div>
          </div>
          <button onClick={close} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 4 }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {/* Step 1: Upload */}
          {step === 1 && (
            <div style={{ textAlign: "center", padding: 30 }}>
              <div onClick={() => fileRef.current?.click()}
                style={{ border: "1.5px dashed var(--border)", borderRadius: 12, padding: "40px 20px", cursor: "pointer", transition: "border-color 0.15s" }}
                onMouseOver={e => e.currentTarget.style.borderColor = "var(--blue)"}
                onMouseOut={e => e.currentTarget.style.borderColor = "var(--border)"}>
                <Upload style={{ width: 28, height: 28, color: "var(--blue)", margin: "0 auto 10px" }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>Drop your CSV file here or click to browse</div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Supports .csv files. Claude AI will analyze and map columns automatically.</div>
              </div>
              <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{ display: "none" }} data-testid="csv-file-input" />

              {/* Demo Mode: Try with sample */}
              <div style={{ marginTop: 16, padding: 14, background: "var(--blue-light)", borderRadius: 10, border: "0.5px solid rgba(0,113,227,0.15)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <Sparkles style={{ width: 12, height: 12, color: "var(--blue)" }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--blue)" }}>Demo mode — try it now</span>
                </div>
                <p style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 10 }}>
                  Use our sample CSV with 8 healthcare practice leads to see how AI analyzes and maps your data.
                </p>
                <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                  <button className="btn" style={{ fontSize: 11, padding: "6px 12px", border: "0.5px solid var(--border)" }}
                    onClick={downloadMockCSV} data-testid="download-sample-csv">
                    <Download style={{ width: 11, height: 11 }} /> Download sample
                  </button>
                  <button className="btn btn-primary" style={{ fontSize: 11, padding: "6px 12px" }}
                    onClick={loadMockCSV} data-testid="try-sample-csv">
                    <ArrowRight style={{ width: 11, height: 11 }} /> Try with sample data
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 12, padding: 12, background: "var(--gray-light)", borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 4 }}>Expected columns</div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.6 }}>
                  Name, Email, Phone, Company, Job Title, Specialty, Deal Value, Notes, etc.
                  <br />Any format works — AI will intelligently map your columns.
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Mapping */}
          {step === 2 && (
            <div>
              {analyzing ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <Loader2 style={{ width: 24, height: 24, color: "var(--blue)", animation: "spin 1s linear infinite", margin: "0 auto 10px" }} />
                  <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>Claude AI is analyzing your CSV...</div>
                </div>
              ) : (
                <>
                  {aiPowered && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "#f8f5ff", borderRadius: 8, marginBottom: 12, border: "0.5px solid rgba(84,72,168,0.15)" }}>
                      <Sparkles style={{ width: 12, height: 12, color: "var(--purple)" }} />
                      <span style={{ fontSize: 11, color: "var(--purple)", fontWeight: 500 }}>AI-powered mapping applied. Review and adjust if needed.</span>
                    </div>
                  )}
                  {/* File info */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "var(--gray-light)", borderRadius: 8, marginBottom: 12 }}>
                    <FileText style={{ width: 14, height: 14, color: "var(--text-tertiary)" }} />
                    <span style={{ fontSize: 12, fontWeight: 560, color: "var(--text-primary)" }}>{file?.name}</span>
                    <div style={{ marginLeft: "auto", display: "flex", gap: 12, fontSize: 11 }}>
                      <span style={{ color: "var(--blue)", fontWeight: 600 }}>{totalRows} rows</span>
                      <span style={{ color: "var(--purple)", fontWeight: 600 }}>{headers.length} columns</span>
                      <span style={{ color: "var(--green)", fontWeight: 600 }}>{mappedCount} mapped</span>
                    </div>
                  </div>

                  {/* Analysis summary */}
                  <div style={{ padding: "8px 12px", background: aiPowered ? "#f8f5ff" : "var(--blue-light)", borderRadius: 8, marginBottom: 12, border: `0.5px solid ${aiPowered ? "rgba(84,72,168,0.15)" : "rgba(0,113,227,0.15)"}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Sparkles style={{ width: 12, height: 12, color: aiPowered ? "var(--purple)" : "var(--blue)" }} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: aiPowered ? "var(--purple)" : "var(--blue)" }}>
                        Claude analyzed {totalRows} leads across {headers.length} columns — {mappedCount} fields auto-mapped
                      </span>
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 3 }}>
                      Review the mapping below. Click any "Map to" dropdown to adjust. Sample data is editable.
                    </div>
                  </div>
                  {/* Mapping table */}
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "0.5px solid var(--border)" }}>
                        <th style={{ padding: "8px 6px", textAlign: "left", fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase" }}>CSV column</th>
                        <th style={{ padding: "8px 6px", textAlign: "left", fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Sample data</th>
                        <th style={{ padding: "8px 6px", textAlign: "left", fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Map to</th>
                      </tr>
                    </thead>
                    <tbody>
                      {headers.map((h, i) => (
                        <tr key={h} style={{ borderBottom: "0.5px solid var(--border)" }}>
                          <td style={{ padding: "8px 6px", fontSize: 12, fontWeight: 560 }}>{h}</td>
                          <td style={{ padding: "8px 6px", fontSize: 11, color: "var(--text-tertiary)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {editingCell === `${i}-sample` ? (
                              <input className="input" autoFocus style={{ fontSize: 11, padding: "2px 4px", width: "100%" }}
                                defaultValue={sampleRows[0]?.[i] || ""}
                                onBlur={e => {
                                  if (sampleRows[0]) {
                                    const updated = [...sampleRows];
                                    updated[0] = [...updated[0]];
                                    updated[0][i] = e.target.value;
                                    setSampleRows(updated);
                                  }
                                  setEditingCell(null);
                                }}
                                onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }} />
                            ) : (
                              <span onClick={() => setEditingCell(`${i}-sample`)} style={{ cursor: "text" }} title="Click to edit">
                                {sampleRows[0]?.[i] || "—"}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "8px 6px" }}>
                            <select className="input" value={mapping[h] || "skip"} onChange={e => updateMapping(h, e.target.value)}
                              data-testid={`mapping-${i}`}
                              style={{ fontSize: 11, padding: "4px 8px", minWidth: 130, color: mapping[h] === "skip" ? "var(--text-tertiary)" : "var(--text-primary)" }}>
                              {CRM_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {/* Step 3: Importing */}
          {step === 3 && (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Loader2 style={{ width: 28, height: 28, color: "var(--blue)", animation: "spin 1s linear infinite", margin: "0 auto 10px" }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>Importing leads...</div>
              <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Creating contacts and lead profiles from your CSV data.</div>
            </div>
          )}

          {/* Step 4: Done */}
          {step === 4 && result && (
            <div style={{ textAlign: "center", padding: 30 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--green-light)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                <Check style={{ width: 22, height: 22, color: "var(--green)" }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 650, color: "var(--text-primary)", marginBottom: 4 }}>Import complete</div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
                {result.imported} leads created from {totalRows} rows
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                <div style={{ padding: "10px 12px", background: "var(--green-light)", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "var(--green)" }}>{result.imported}</div>
                  <div style={{ fontSize: 10, color: "var(--green)" }}>Leads created</div>
                </div>
                <div style={{ padding: "10px 12px", background: "var(--blue-light)", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "var(--blue)" }}>{mappedCount}</div>
                  <div style={{ fontSize: 10, color: "var(--blue)" }}>Fields mapped</div>
                </div>
                <div style={{ padding: "10px 12px", background: "var(--amber-light)", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "var(--amber)" }}>{result.skipped || 0}</div>
                  <div style={{ fontSize: 10, color: "var(--amber)" }}>Skipped</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "var(--amber-light)", borderRadius: 8, border: "0.5px solid rgba(176,106,20,0.15)" }}>
                <AlertTriangle style={{ width: 12, height: 12, color: "var(--amber)" }} />
                <span style={{ fontSize: 11, color: "var(--amber)", fontWeight: 500 }}>
                  Imported leads may have missing info. Check the notification banner on the Leads page.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: "0.5px solid var(--border)", padding: "12px 20px", display: "flex", justifyContent: "flex-end", gap: 6 }}>
          <button className="btn" onClick={close}>{step === 4 ? "Done" : "Cancel"}</button>
          {step === 2 && !analyzing && (
            <button className="btn btn-primary" onClick={doImport} disabled={mappedCount === 0} data-testid="csv-import-btn">
              <Upload style={{ width: 12, height: 12 }} /> Import {totalRows} leads ({mappedCount} fields)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
